import { ChangeDetectionStrategy, Component, Input, ViewEncapsulation } from '@angular/core';
import { PhxTextDecoration, PhxTextIntent, PhxTextSize } from './phx-text.types';
import { PHX_SIZE } from '../../constants';

/**
 * Reusable typography primitive mirroring the Figma "Label" component: `phxSize`
 * controls the typography scale, `phxTextIntent` controls colour (default
 * `standard`), and emphasis / italic / underline / dotted are orthogonal toggles.
 * Usable anywhere — not tied to the table.
 */
@Component({
  selector: 'phx-text',
  template: '<ng-content></ng-content>',
  styleUrls: ['./phx-text.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  host: {
    class: 'phx-text',
    '[class.phx-text-medium]': 'phxSize === PHX_SIZE.MD',
    '[class.phx-text-small]': 'phxSize === PHX_SIZE.SM',
    '[class.phx-text-extra-small]': 'phxSize === PHX_SIZE.XS',
    '[class.phx-text-emphasis]': 'phxEmphasis',
    '[class.phx-text-italic]': 'phxItalic',
    '[class.phx-text-intent-standard]': 'phxTextIntent === "standard"',
    '[class.phx-text-intent-subdued]': 'phxTextIntent === "subdued"',
    '[class.phx-text-intent-active]': 'phxTextIntent === "active"',
    '[class.phx-text-intent-positive]': 'phxTextIntent === "positive"',
    '[class.phx-text-intent-negative]': 'phxTextIntent === "negative"',
    '[class.phx-text-intent-caution]': 'phxTextIntent === "caution"',
    '[class.phx-text-intent-inactive]': 'phxTextIntent === "inactive"',
    '[class.phx-text-dotted]': 'phxDecoration === "dotted"',
    '[class.phx-text-underline]': 'phxDecoration === "underline"',
    '[class.phx-text-truncate]': 'phxTruncate && phxMaxLines == null',
    '[class.phx-text-underline-on-hover]': 'phxUnderlineOnHover',
    '[class.phx-text-clamp]': 'phxMaxLines != null',
    '[style.-webkit-line-clamp]': 'phxMaxLines',
  },
})
export class PhxTextComponent {
  /** Typography size. Default `'medium'`. */
  @Input() phxSize: PhxTextSize = PHX_SIZE.MD;
  /** Text color intent. Default `'standard'`. */
  @Input() phxTextIntent: PhxTextIntent = 'standard';
  /** When true, applies the typography emphasis style for the current `phxSize`. */
  @Input() phxEmphasis: boolean = false;
  /** When true, renders the text in italic. */
  @Input() phxItalic: boolean = false;
  /** Optional underline style (`'dotted'` or `'underline'`). */
  @Input() phxDecoration: PhxTextDecoration | undefined;
  /** When true, truncates overflowing text with an ellipsis. */
  @Input() phxTruncate: boolean = false;
  /** When true, shows a solid underline on hover regardless of the current decoration. */
  @Input() phxUnderlineOnHover: boolean = false;
  /**
   * When set, clamps the text to this many lines (multi-line ellipsis) instead of
   * the single-line `phxTruncate`. Overrides `phxTruncate` when both are provided.
   */
  @Input() phxMaxLines: number | undefined;

  public readonly PHX_SIZE = PHX_SIZE;
}
